/**
 * Socket Auth Manager
 *
 * Manages token lifecycle for Socket.IO connections:
 * - Smart token refresh (checks JWT expiry before making a network call)
 * - Auth rejection tracking (stops retrying after repeated server rejections)
 * - Classifies error codes from the shared error catalog
 *
 * Dependencies are injected for testability — no direct imports of
 * auth providers, Socket.IO, or other singletons.
 */

import socketErrors from '../generated/shared/socketErrors.json';

/**
 * Auth error codes — single source of truth shared with backend.
 *
 * Deliberately `authErrors` ONLY. The catalog's other array, `transientErrors`
 * (today: `AUTH_SERVER_UNAVAILABLE`), must stay out: a code in this set
 * increments `consecutiveAuthRejections` and hard-stops the socket at three,
 * which is the right response to a token the server refuses and the wrong one
 * to a server that could not answer. Widening this to every code in the
 * catalog would turn a Supabase Auth outage into "session expired" on every
 * client — precisely what the backend split those arrays to prevent.
 */
export const AUTH_ERROR_CODES = new Set(socketErrors.authErrors);

/** Safety margin: refresh token if it expires within this window */
const TOKEN_EXPIRY_MARGIN_S = 60;

/** Stop force-refreshing after this many consecutive server rejections */
const MAX_AUTH_REJECTIONS = 3;

/** Injected auth provider — token access interface */
export interface AuthProvider {
  getSession(): Promise<{ data: { session: { access_token: string } | null } }>;
  refreshSession(): Promise<{ data: { session: { access_token: string } | null }; error: { message: string } | null }>;
}

export class SocketAuthManager {
  private consecutiveAuthRejections = 0;
  private readonly getAuth: () => AuthProvider;

  /**
   * @param getAuth - Factory that returns the auth provider. Called lazily on
   *   each token operation, not at construction time. This avoids eagerly
   *   initializing the auth provider at module load.
   */
  constructor(getAuth: () => AuthProvider) {
    this.getAuth = getAuth;
  }

  /**
   * Get a valid access token for socket authentication.
   *
   * 1. Reads the cached session (no network call)
   * 2. Checks JWT `exp` claim locally (base64 decode, no library)
   * 3. If still valid (with 60s margin) and not force-refreshing, returns cached token
   * 4. If expired, near expiry, or server-rejected, calls refreshSession() (network call)
   * 5. Returns null if no session or refresh fails
   *
   * @param forceRefresh - Force a refresh regardless of expiry (e.g., server rejected the token)
   */
  async getFreshToken(forceRefresh = false): Promise<string | null> {
    // getSession() reads from memory — no network call
    const { data: { session } } = await this.getAuth().getSession();

    if (!session?.access_token) return null;

    // Return cached token if valid and not force-refreshing
    if (!forceRefresh && isTokenValid(session.access_token)) {
      return session.access_token;
    }

    // refreshSession() makes a network call to get a new token
    const { data, error } = await this.getAuth().refreshSession();
    if (error || !data.session?.access_token) {
      return null;
    }

    return data.session.access_token;
  }

  /**
   * Handle a connection error and determine if the token should be refreshed.
   *
   * Returns the action to take:
   * - `{ action: 'refresh', token }` — use this fresh token for the next attempt
   * - `{ action: 'stop' }` — max auth rejections reached, stop retrying
   * - `{ action: 'none' }` — no token refresh needed or possible
   */
  async handleConnectError(errorCode: string | undefined): Promise<
    | { action: 'refresh'; token: string }
    | { action: 'stop' }
    | { action: 'retry' }
    | { action: 'none' }
  > {
    const serverRejected = errorCode ? AUTH_ERROR_CODES.has(errorCode) : false;

    // Transport errors (no auth error code) — let Socket.IO retry as-is.
    // No point refreshing the token when the problem is the network.
    if (!serverRejected) {
      return { action: 'retry' };
    }

    this.consecutiveAuthRejections++;

    if (this.consecutiveAuthRejections >= MAX_AUTH_REJECTIONS) {
      return { action: 'stop' };
    }

    const freshToken = await this.getFreshToken(true);
    if (freshToken) {
      return { action: 'refresh', token: freshToken };
    }

    // Auth error but can't get a fresh token — session is gone
    return { action: 'none' };
  }

  /** Reset the auth rejection counter (call on successful connect or manual reconnect). */
  resetRejections(): void {
    this.consecutiveAuthRejections = 0;
  }

  /** Current consecutive auth rejection count (for testing/logging). */
  get rejectionCount(): number {
    return this.consecutiveAuthRejections;
  }
}

/**
 * Check if a JWT is still valid by decoding the `exp` claim.
 * Returns false if expired, near expiry, or unparseable.
 */
export function isTokenValid(token: string): boolean {
  try {
    const payload = token.split('.')[1];
    if (!payload) return false;
    const decoded = JSON.parse(atob(payload));
    const now = Math.floor(Date.now() / 1000);
    return decoded.exp > now + TOKEN_EXPIRY_MARGIN_S;
  } catch {
    return false;
  }
}
